/**
 * @file        KybManagerUsersPage.jsx
 * @module      modules/kyb/manager/pages
 * @description "KYB Managers" menu for ASAP admins (/kyb-manager-users):
 *              list KYB manager accounts, create one (a password is generated
 *              and e-mailed, like Create KAM), activate / deactivate it and
 *              send a new password. A KYB manager signs in with that e-mail and
 *              password and lands on the KYB Manager. Reviewers (role
 *              kyb_executive) get no row of their own: each manager's row
 *              carries its team, shown by the Reviewers count or the gear's
 *              "View reviewers".
 */

import React, { useCallback, useEffect, useState } from "react";
import { Alert, Dropdown, Empty, Form, Input, InputNumber, Modal, Spin, Table, Tag, message } from "antd";
import { KeyRound, Search, Settings, UserCheck, UserPlus, UserX, Users } from "lucide-react";
import { kybManagerApi } from "../api/kybManagerApi";
import { fmtDate } from "../components/Labels";
import CustomeHeader from "../../../../components/Admin/Common/CustomeHeader";
import CarrierPagination from "../../../../components/Admin/CarrierManagement/CarrierPagination";
import { MainContainer } from "../../../../StyledComponents/Dashboard";
import "../../../../components/BookingHistory/AirCargoList.css";
import "../styles/kybManager.css";

const DEFAULT_PAGE_SIZE = 10;

const mobileOf = (u) => (u.mobile ? `${u.countryCode ? `+${u.countryCode} ` : ""}${u.mobile}` : "—");

// Each manager row carries its own reviewers (role kyb_executive) as `team`,
// in the same shape as the manager itself — [] when they have none.
const reviewersOf = (u) => u?.team || [];

function CreateModal({ open, onClose, onCreated }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const res = await kybManagerApi.createUser({ ...values, phone_number: String(values.phone_number).replace(/\D/g, "") });
      if (res.emailed) message.success(`KYB manager created. The password was e-mailed to ${res.user.email}.`);
      else message.warning("KYB manager created, but the password e-mail could not be sent. Use “Send new password”.");
      form.resetFields();
      onCreated(res.user);
    } catch (e) {
      if (e.field) form.setFields([{ name: e.field, errors: [e.message] }]);
      else message.error(e.message || "Couldn't create the KYB manager.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} title="Add KYB manager" okText="Create and e-mail password" onOk={submit} confirmLoading={saving} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" initialValues={{ country_code: 91 }} preserve={false}>
        <Form.Item name="full_name" label="Full name" rules={[{ required: true, whitespace: true, message: "Enter the full name." }, { max: 50 }]}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="email" label="E-mail" rules={[{ required: true, message: "Enter the e-mail address." }, { type: "email", message: "Enter a valid e-mail address." }]}>
          <Input autoComplete="off" />
        </Form.Item>
        <div style={{ display: "flex", gap: 8 }}>
          <Form.Item name="country_code" label="Code" style={{ width: 100 }} rules={[{ required: true, message: "Code" }]}>
            <InputNumber min={1} max={999} prefix="+" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="phone_number"
            label="Mobile"
            style={{ flex: 1 }}
            rules={[
              { required: true, message: "Enter the mobile number." },
              { pattern: /^\d{7,12}$/, message: "Digits only (7–12)." },
            ]}
          >
            <Input inputMode="numeric" autoComplete="off" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

/**
 * The reviewers one manager has added to their team, read-only — a manager
 * adds and removes them from the KYB console, not from here.
 */
function ReviewersModal({ data, onClose }) {
  const manager = data?.manager;
  return (
    <Modal open={!!data} onCancel={onClose} footer={null} width={720} destroyOnHidden title={manager ? `Reviewers in ${manager.name}'s team` : "Reviewers"}>
      {data?.items?.length ? (
        <div className="tableResponsiveASAP">
          <Table
            className="kybmTable"
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={data.items}
            columns={[
              { title: "Name", dataIndex: "name" },
              { title: "E-mail", dataIndex: "email" },
              { title: "Mobile", render: (_, u) => mobileOf(u) },
              { title: "Status", dataIndex: "active", render: (a) => (a ? <Tag color="success">Active</Tag> : <Tag>Deactivated</Tag>) },
              { title: "Added", dataIndex: "createdAt", render: fmtDate },
            ]}
          />
        </div>
      ) : (
        <Empty description={manager ? `${manager.name} hasn't added any reviewers yet.` : "No reviewers"} />
      )}
    </Modal>
  );
}

export default function KybManagerUsersPage() {
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: null });
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);
  // { manager, items } while the reviewers modal is open.
  const [reviewers, setReviewers] = useState(null);

  // The search box filters as you type; wait for a pause so a keystroke isn't
  // one request.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setQ(searchText.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    kybManagerApi
      // Managers only — a reviewer belongs to a manager's team and is reached
      // through that manager's "View reviewers", so the list is filtered by the
      // API's own `role` filter rather than trimmed here.
      .users({ q, role: "kyb_manager", page, limit: pageSize })
      .then((r) => setState({ items: r.items, total: r.total, loading: false, error: null }))
      .catch((e) => setState({ items: [], total: 0, loading: false, error: e.status === 401 ? "Only ASAP admins can manage KYB managers." : e.message }));
  }, [q, page, pageSize]);

  useEffect(load, [load]);

  const setActive = async (u, active) => {
    setBusy(u.id);
    try {
      await kybManagerApi.setUserActive(u.id, active);
      message.success(active ? `${u.name} can sign in again.` : `${u.name} can no longer sign in.`);
      load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const resend = async (u) => {
    setBusy(u.id);
    try {
      await kybManagerApi.resendPassword(u.id);
      message.success(`A new password was e-mailed to ${u.email}.`);
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  // The reviewers come down with the manager's own row, so the modal opens
  // straight away — no second request.
  const openReviewers = (u) => setReviewers({ manager: u, items: reviewersOf(u) });

  const actionItems = (u) => [
    { key: "reviewers", icon: <Users size={14} />, label: "View reviewers" },
    { key: "password", icon: <KeyRound size={14} />, label: "Send new password", disabled: !u.active },
    { type: "divider" },
    u.active
      ? { key: "deactivate", icon: <UserX size={14} />, label: "Deactivate", danger: true }
      : { key: "activate", icon: <UserCheck size={14} />, label: "Activate" },
  ];

  const onAction = (u, key) => {
    if (key === "reviewers") return openReviewers(u);
    if (key === "password")
      return Modal.confirm({
        title: "Send a new password?",
        content: `A new password will be generated and e-mailed to ${u.email}.`,
        okText: "Send",
        onOk: () => resend(u),
      });
    if (key === "deactivate")
      return Modal.confirm({
        title: `Deactivate ${u.name}?`,
        content: "They won't be able to sign in until the account is activated again.",
        okText: "Deactivate",
        okButtonProps: { danger: true },
        onOk: () => setActive(u, false),
      });
    return setActive(u, true);
  };

  const columns = [
    { title: "S.No", width: 70, render: (_, __, i) => (page - 1) * pageSize + i + 1 },
    { title: "Name", dataIndex: "name" },
    { title: "E-mail", dataIndex: "email" },
    { title: "Mobile", render: (_, u) => <span className="kybm-nowrap">{mobileOf(u)}</span> },
    {
      title: "Reviewers",
      width: 110,
      render: (_, u) => {
        const team = reviewersOf(u);
        return team.length ? (
          <button type="button" className="kybm-count" onClick={() => openReviewers(u)} title={`View the ${team.length} reviewer(s) in ${u.name}'s team`}>
            {team.length}
          </button>
        ) : (
          "—"
        );
      },
    },
    { title: "Status", dataIndex: "active", render: (a) => (a ? <Tag color="success">Active</Tag> : <Tag>Deactivated</Tag>) },
    { title: "Added", dataIndex: "createdAt", render: (d) => <span className="kybm-nowrap">{fmtDate(d)}</span> },
    {
      title: "Actions",
      width: 90,
      render: (_, u) => (
        <Dropdown menu={{ items: actionItems(u), onClick: ({ key }) => onAction(u, key) }} trigger={["click"]} placement="bottomRight" overlayClassName="kybm-actions-menu">
          <button type="button" className="kybm-gear" aria-label={`Actions for ${u.name}`}>
            {busy === u.id ? <Spin size="small" /> : <Settings size={16} />}
          </button>
        </Dropdown>
      ),
    },
  ];

  return (
    // Same page chrome as Carrier Management: the shared admin header carries
    // the title, the notification bell and the profile / logout dropdown.
    <MainContainer>
      <CustomeHeader
        title="KYB Managers"
        description="KYB manager accounts and the size of each team. Open a manager's reviewers to see who they have added from the KYB console."
      />
      <section className="kybm">
        <div className="kybm-tabletop">
          <span className="kybm-tabletitle">Manager List</span>
          <div className="kybm-toolbar">
            <Input
              allowClear
              prefix={<Search size={15} color="#a8a29e" />}
              placeholder="Search by name, e-mail or mobile"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="carrierSearch kybm-search"
              aria-label="Search KYB managers"
            />
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="text-white bg-primary hover:bg-secondary font-medium rounded-lg text-sm px-4 py-2 text-center flex items-center justify-center gap-2 shadow-searchbox"
            >
              <UserPlus size={15} />
              <span>Add KYB manager</span>
            </button>
          </div>
        </div>
        {state.error && <Alert type="error" showIcon message={state.error} style={{ marginBottom: 12 }} />}
        <div className="mt-4 tableResponsiveASAP">
          <Table
            className="bookingHistoryTable kybmTable"
            rowKey="id"
            loading={state.loading}
            dataSource={state.items}
            columns={columns}
            pagination={false}
            scroll={{ x: 820 }}
          />
          <CarrierPagination
            total={state.total}
            currentPage={page}
            pageSize={pageSize}
            noun="users"
            onPageChange={setPage}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        </div>
      <ReviewersModal data={reviewers} onClose={() => setReviewers(null)} />
      <CreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          setPage(1);
          load();
        }}
      />
      </section>
    </MainContainer>
  );
}
